<template>
  <nav id="navigation" aria-label="Main">
    <button
      class="relative flex-c focus-a z-1"
      :aria-label="isOpen ? 'Close' : 'Open'"
      aria-controls="main-navigation"
      :aria-expanded="isOpen"
      data-gtm-hamburger-menu
      @click="handleClick"
      @focus="handleFocus($event)"
    >
      <IconBase
        :icon="isOpen ? 'CloseIcon' : 'BurgerIcon'"
        color="primary"
        :size="mobile ? '2rem' : '2.2rem'"
      />
    </button>

    <Transition name="slide-left">
      <div
        v-show="isOpen"
        id="main-navigation"
        class="f-cover bg-white overflow-auto"
      >
        <div class="md:h-full">
          <div class="flex md:h-full md:pb-20">
            <!-- Only shows the title and image graphic on desktop -->
            <div v-if="!mobile" class="h-full w-65% flex flex-col bg-primary">
              <div
                role="img"
                :alt="image.alt"
                class="bg-grey-1 h-70% bg-full"
                :style="`background-image: url(${image.src})`"
              />
              <div class="relative flex-grow">
                <Title class="pin-title" />
              </div>
            </div>

            <div
              class="mx-auto lg:relative md:h-full md:flex-c md:flex-grow bg-white"
            >
              <a
                :href="utmHref('https://www.liverpool.ac.uk/', 'logo-click')"
                aria-label="University of Liverpool"
                target="_blank"
                class="absolute top-3 left-3 md:top-4 md:left-8 h-8 md:h-10 block focus-a"
              >
                <img
                  src="@/assets/logo-full.svg"
                  alt="University logo"
                  class="h-full w-auto"
                />
              </a>
              <div class="w-64 mt-20 mb-10 md:mb-0 md:mt-12">
                <ul>
                  <li>
                    <RouterLink
                      to="/"
                      class="relative h-12 px-3 flex-a justify-between br-grey-2 border-b border-body font-bold bg-grey-2 focus-b"
                    >
                      Home
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" v-bind:svg-inline="''" v-bind:class="'h-6 w-6 rotate-180'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="none" stroke="#09104a" stroke-miterlimit="10" stroke-width="1.391" d="M.696 16C.696 7.548 7.548.696 16 .696S31.304 7.548 31.304 16 24.452 31.304 16 31.304.696 24.452.696 16z"/><path fill="none" stroke="#09104a" stroke-miterlimit="10" stroke-width="1.391" d="M9.043 16l11.965 7.652c-.839-2.272-1.345-4.896-1.391-7.632v-.021a37.092 37.092 0 011.459-7.915l-.068.262z"/></svg>
                    </RouterLink>
                  </li>
                  <!-- <li> -->
                  <!--   <div -->
                  <!--     class="relative h&#45;12 px&#45;3 flex&#45;a justify&#45;between br&#45;grey&#45;2 border&#45;b border&#45;body font&#45;bold bg&#45;grey&#45;2 tooltip focus&#45;b" -->
                  <!--   > -->
                  <!--     Explore for yourself -->
                  <!--     <img -->
                  <!--       svg&#45;inline -->
                  <!--       src="@/assets/icon&#45;chevron&#45;circle.svg" -->
                  <!--       alt="" -->
                  <!--       class="h&#45;6 w&#45;6 rotate&#45;180" -->
                  <!--     /> -->
                  <!--   </div> -->
                  <!-- </li> -->
                  <li v-for="category in categories" :key="category.slug">
                    <RouterLink
                      :to="{
                        name: 'campus-category',
                        params: { category_id: category.slug }
                      }"
                      class="h-12 flex-a px-3 border-b border-body bg-grey-1 focus-b hover:bg-grey-2 focus:bg-grey-2"
                      @click.native="isOpen = !isOpen"
                    >
                      {{ category.title }}
                    </RouterLink>
                  </li>
                  <li class="mb-8">
                    <RouterLink
                      to="/guided-tour"
                      class="relative h-12 px-3 flex-a justify-between br-grey-2 border-b border-body font-bold focus-b hover:bg-grey-2 focus:bg-grey-2"
                      @click.native="isOpen = !isOpen"
                    >
                      Start a guided tour
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" v-bind:svg-inline="''" v-bind:class="'h-6 w-6 rotate-180'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill="none" stroke="#09104a" stroke-miterlimit="10" stroke-width="1.391" d="M.696 16C.696 7.548 7.548.696 16 .696S31.304 7.548 31.304 16 24.452 31.304 16 31.304.696 24.452.696 16z"/><path fill="none" stroke="#09104a" stroke-miterlimit="10" stroke-width="1.391" d="M9.043 16l11.965 7.652c-.839-2.272-1.345-4.896-1.391-7.632v-.021a37.092 37.092 0 011.459-7.915l-.068.262z"/></svg>
                    </RouterLink>
                  </li>
                  <li
                    v-for="(item, i) in options.calls_to_action"
                    :key="`${item.href}-${i}`"
                    class="mb-2"
                  >
                    <Button
                      type="external"
                      :text="item.call_to_action"
                      :path="item.href"
                      :utm="`tier-1-cta`"
                      class="btn-sm btn-primary justify-center"
                    />
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <footer
            class="p-x md:absolute md:bottom-0 md:flex-a md:justify-between w-full py-4 md:h-20 bg-body"
          >
            <ul
              aria-label="Additional information links"
              class="text-white text-sm font-bold"
            >
              <li class="inline-block mr-2 mb-2 md:mb-0">
                <span>&copy;</span>
                <a
                  :href="utmHref('https://www.liverpool.ac.uk/', 'tier-2-cta')"
                  target="_blank"
                  class="inline-block ml-1 underline focus-a"
                >
                  University of Liverpool
                </a>
                <span class="font-regular">
                  - a member of the Russel Group
                </span>
              </li>
              <li
                v-for="(item, i) in options.footer_links"
                :key="item.linkout"
                class="inline-block"
              >
                <a
                  class="underline focus-a"
                  :href="utmHref(item.linkout, 'tier-2-cta')"
                  target="_blank"
                  v-html="item.call_to_action"
                />
                <span v-if="i !== options.footer_links.length - 1" class="mx-1">
                  /
                </span>
              </li>
            </ul>

            <div class="flex flex-col justify-center mt-3 md:mt-0">
              <p
                class="inline-block mx-auto text-white text-sm mb-1 focus-a"
                v-html="options.telephone"
              />
              <ul aria-label="Social media links" class="flex justify-between">
                <li
                  v-for="(item, key) in options.social_media"
                  v-show="item"
                  :key="key"
                  class="mr-1 last:mr-0"
                >
                  <Button
                    type="external"
                    :aria="key"
                    :path="item"
                    :utm="`social-link`"
                    :icon="`${key}icon`"
                    @focus.native="handleFocus($event)"
                  />
                </li>
              </ul>
            </div>
          </footer>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import IconBase from '@/components/icons/IconBase.vue'
import Title from '@/components/Title.vue'
import Button from '@/components/buttons/Button.vue'

export default {
  name: 'Navigation',

  components: {
    IconBase,
    Title,
    Button
  },

  data() {
    return {
      isOpen: false
    }
  },

  computed: {
    ...mapState(['touch', 'mobile']),
    ...mapGetters(['options', 'categories']),

    image() {
      return {
        src: this.options?.image?.sizes?.large,
        alt: this.options?.image?.alt
      }
    }
  },

  methods: {
    handleClick() {
      this.isOpen = !this.isOpen
      this.$store.commit('toggleNavState')
    },

    handleFocus() {
      /*
       * Traps focus within the menu. Make sure to add the
       * focus handle on the first and last focusable elements
       */

      const domNode = document.getElementById('navigation')
      const focusableEls = [...domNode.querySelectorAll('button, a')]
      const firstFocusableEl = focusableEls[0]
      const lastFocusableEl = focusableEls[focusableEls.length - 1]

      event.target.addEventListener('keydown', e => {
        const isTabPressed = e.code === 'Tab'

        if (!isTabPressed) {
          return
        }

        if (e.shiftKey) {
          if (document.activeElement === firstFocusableEl && this.isOpen) {
            lastFocusableEl.focus()
            e.preventDefault()
          }
        } else {
          if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus()
            e.preventDefault()
          }
        }
      })
    }
  }
}
</script>
